(function ($, window, document) {
    'use strict';

    /**
     * Configuration object for social login functionality.
     * Contains settings and localization messages used during the social login process.
     * Values can either be dynamically injected from the `window.WayGenAISocialLogin` object or fallback to defaults.
     *
     * @property {string} apiBase - URL endpoint for the social login API.
     * @property {string} redirect - URL to redirect users after a successful login.
     * @property {Object} i18n - Localization strings for messages during the social login process.
     * @property {string} i18n.success - Message displayed on successful login.
     * @property {string} i18n.fallback - Message displayed when the login process fails.
     * @property {string} i18n.tokenInvalid - Message displayed when the user's session expires.
     * @property {string} i18n.emailConflict - Message displayed when the email address is already in use.
     * @property {string} i18n.popupBlocked - Message displayed if the login popup is blocked by the browser.
     * @property {string} i18n.facebookCancelled - Message displayed if the Facebook login process is cancelled by the user.
     * @property {string} googleClientId - Google OAuth client ID.
     * @property {string} appleClientId - Apple OAuth client ID.
     * @property {string} appleRedirect - URL for the Apple login callback endpoint.
     * @property {string} facebookAppId - Facebook application ID.
     */
    const CFG = {
        apiBase: window.WayGenAISocialLogin?.apiBase || '/user/social-login',
        accountsApiBase: window.WayGenAISocialLogin?.accountsApiBase || '/user/social-accounts',
        mode: window.WayGenAISocialLogin?.mode || 'login',
        redirect: window.WayGenAISocialLogin?.redirect || '/user/',
        i18n: Object.assign({
            success: 'Logged in successfully. Redirecting…',
            fallback: 'Could not complete social login. Please try again.',
            tokenInvalid: 'Your social session expired. Please try again.',
            emailConflict: 'Email already used by another account.',
            linkRequired: 'This email already has an account. Log in first, then connect the provider from My Account.',
            popupBlocked: 'Please enable popups to continue.',
            facebookCancelled: 'Facebook login was cancelled.',
            connected: 'Connected',
            notConnected: 'Not connected',
            connectSuccess: 'Sign-in method connected.',
            disconnectSuccess: 'Sign-in method disconnected.',
            confirmDisconnect: 'Disconnect this sign-in method?',
            invalidSession: 'Your session has expired. Please log in again.',
        }, window.WayGenAISocialLogin?.i18n || {}),
        googleClientId: window.WayGenAISocialLogin?.googleClientId || '',
        appleClientId: window.WayGenAISocialLogin?.appleClientId || '',
        appleRedirect: window.WayGenAISocialLogin?.appleRedirect || window.location.origin + '/auth/callback/apple',
        facebookAppId: window.WayGenAISocialLogin?.facebookAppId || ''
    };
    const PROVIDER_ENABLED = {
        google: Boolean(CFG.googleClientId),
        apple: Boolean(CFG.appleClientId),
        facebook: Boolean(CFG.facebookAppId)
    };

    /**
     * Redirects to a given URL in a safe and controlled manner, ensuring it conforms to certain rules.
     * If the URL is invalid, uses the fallback instead.
     * This function ensures the URL remains within the same origin and follows specified protocols.
     *
     * @param {string} url The target URL to redirect to.
     * @param {Object} [options] Additional options for the redirection.
     * @param {string} [options.fallback='/user/'] The fallback path to use if the target URL is invalid or unsafe.
     * @param {boolean} [options.keepQuery=false] Whether to retain the query string from the original URL.
     * @return {string} The normalized safe URL to redirect to, or the fallback if the original URL is unsafe or invalid.
     */
    function safeRedirect(url, {fallback = '/user/', keepQuery = false} = {}) {
        try {
            const base = window.location.origin;
            const u = new URL(typeof url === 'string' ? url : '', base);
            if (u.protocol !== 'https:' && u.protocol !== 'http:') return fallback;
            if (u.origin !== base) return fallback;
            const path = u.pathname.startsWith('/') ? u.pathname : '/' + u.pathname;
            const normalized = path.endsWith('/') ? path : path + '/';
            return normalized + (keepQuery ? u.search : '');
        } catch {
            return fallback;
        }
    }

    /**
     * Stores the user data in the browser's local storage.
     *
     * @param {Object} user The user object to be stored.
     * @return {void} Does not return a value.
     */
    function setUser(user) {
        try {
            localStorage.setItem('wg_user', JSON.stringify(user));
        } catch (_) {
        }
    }

    /**
     * Displays a global error message based on the availability of specific elements or defaults to an alert box.
     *
     * @param {string} msg The error message to display.
     * @return {void} This function does not return any value.
     */
    function showGlobalError(msg) {
        if ($('#social-accounts-error').length) {
            $('#social-accounts-error').show().find('span').text(msg);
            $('#social-accounts-success').hide().find('span').text('');
            return;
        }
        if ($('#login-global-error').length) {
            $('#login-success').hide().text('');
            $('#login-global-error').text(msg).show();
            return;
        }
        if ($('#reg-social-global-error').length) {
            $('#reg-social-global-error').text(msg).show();
            return;
        }
        alert(msg);
    }

    /**
     * Displays success messages on appropriate elements based on their availability.
     *
     * @param {string} msg - The success message to display.
     * @return {void} - No explicit return value.
     */
    function showSuccess(msg) {
        if ($('#social-accounts-success').length) {
            $('#social-accounts-success').show().find('span').text(msg);
            $('#social-accounts-error').hide().find('span').text('');
            return;
        }
        if ($('#login-success').length) {
            $('#login-global-error').hide().text('');
            $('[data-error-for]').text('');
            $('#email, #password').removeClass('is-invalid');
            $('#login-success').text(msg).show();
            return;
        }
        if ($('#reg-social-success-panel').length) {
            const $panel = $('#reg-social-success-panel').show();
            $panel.find('.auth-subtitle').text(msg)
                .prev('.auth-title').text(''); // опционально
            $panel.closest('.auth-card').find('>.auth-title, >.auth-subtitle, >.auth-footer').hide();
            return;
        }
    }

    /**
     * Toggles the loading state of a button by enabling/disabling it and showing/hiding a spinner icon.
     *
     * @param {jQuery} $btn - The jQuery object representing the button element.
     * @param {boolean} on - A boolean indicating whether to enable the loading state (true) or disable it (false).
     * @return {void} This function does not return any value.
     */
    function setBtnLoading($btn, on) {
        if (!$btn || !$btn.length) return;
        $btn.prop('disabled', on);
        $btn.find('.spinner').toggle(on);
    }

    /**
     * Sends a POST request to perform social login using the provided vendor and token information.
     * The method includes additional user data such as first name and last name if available.
     *
     * @param {Object} params - The parameters for the social login.
     * @param {string} params.vendor - The vendor through which the social login is performed (e.g., Google, Facebook).
     * @param {string} params.token - The authentication token provided by the social login vendor.
     * @param {string} [params.first_name] - The user's first name, if available.
     * @param {string} [params.last_name] - The user's last name, if available.
     * @return {Promise<Object>} Returns a promise that resolves to the response data from the API if the login succeeds.
     * @throws {Error} Throws an error if the API request fails or returns a non-OK status.
     */
    async function postSocialLogin({vendor, token, first_name, last_name}) {
        const payload = {
            vendor,
            token,
            first_name: first_name || undefined,
            last_name: last_name || undefined
        };
        const res = await fetch(CFG.mode === 'account' ? CFG.accountsApiBase : CFG.apiBase, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            credentials: 'include',
            body: JSON.stringify(payload)
        });
        const isJson = (res.headers.get('content-type') || '').includes('application/json');
        const data = isJson ? await res.json().catch(() => ({})) : {};
        if (!res.ok) {
            const detail = data?.detail;
            const msg = typeof detail === 'string' ? detail : detail?.message || CFG.i18n.fallback;
            const err = new Error(msg);
            err.status = res.status;
            err.code = typeof detail === 'object' ? detail?.code : null;
            throw err;
        }
        return data;
    }

    /**
     * Handles the success response from a server operation, typically by processing the received data,
     * updating the user state, showing a success message, and redirecting the user.
     *
     * @param {Object} data - The response data received from the server.
     * @param {Object} [data.user] - The user-related information in the response.
     * @return {void} This function does not return a value.
     */
    function handleSuccess(data) {
        if (CFG.mode === 'account') {
            renderSocialAccounts(data);
            showSuccess(CFG.i18n.connectSuccess);
            return;
        }
        setUser(data.user || data);
        showSuccess(CFG.i18n.success);
        setTimeout(function () {
            window.location.assign(safeRedirect(CFG.redirect, {fallback: '/user/'}));
        }, 600);
    }

    /**
     * Handles and displays appropriate error messages based on the input error object.
     *
     * @param {Object} err - The error object containing information about the error.
     * @param {number} [err.status] - The HTTP status code of the error (e.g., 409, 0).
     * @param {string} [err.message] - The error message.
     * @return {void} This function does not return a value.
     */
    function handleError(err) {
        if (err?.status === 409) {
            showGlobalError(err.code === 'social_link_required' ? CFG.i18n.linkRequired : err.message || CFG.i18n.emailConflict);
            return;
        }
        if (err?.status === 401) {
            showGlobalError(CFG.i18n.invalidSession);
            return;
        }
        if (err?.status === 0) {
            showGlobalError(CFG.i18n.fallback);
            return;
        }
        showGlobalError(err?.message || CFG.i18n.fallback);
    }

    /**
     * Renders and updates the visibility of social account elements based on the provided account data.
     *
     * @param {Object} data An object containing social account information.
     * @param {Array} data.accounts An array of account objects, where each object includes details about a social account.
     * @return {void} This method does not return a value.
     */
    function renderSocialAccounts(data) {
        const accounts = new Map((data?.accounts || []).map(account => [account.provider, account]));
        $('[data-social-account]').each(function () {
            const $row = $(this);
            const provider = $row.data('social-account');
            const account = accounts.get(provider);
            const providerEnabled = PROVIDER_ENABLED[provider];
            if (!account && !providerEnabled) {
                $row.hide();
                return;
            }
            $row.show();
            $row.removeAttr('hidden');
            $row.find('[data-social-status]')
                .text(account?.email ? CFG.i18n.connected + ' — ' + account.email : account ? CFG.i18n.connected : CFG.i18n.notConnected)
                .removeClass('is-loading');
            $row.find('[data-social]').toggle(!account && providerEnabled);
            $row.find('[data-social-disconnect]').toggle(!!account);
            $row.find('[data-social-placeholder]').hide();
        });
        const $section = $('.wg-social-accounts');
        $section.toggle($section.find('.wg-social-account:visible').length > 0);
    }

    /**
     * Loads social account data using the current cookie-backed session.
     *
     * @return {Promise<void>} A promise that resolves when the social account data is successfully loaded and rendered.
     * @throws {Error} Throws an error if the current session is invalid or the API request fails.
     */
    async function loadSocialAccounts() {
        const res = await fetch(CFG.accountsApiBase, {
            credentials: 'include'
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            const detail = data?.detail;
            throw Object.assign(
                new Error(typeof detail === 'string' ? detail : CFG.i18n.fallback),
                {status: res.status}
            );
        }
        renderSocialAccounts(data);
    }

    /**
     * Handles the disconnection event when a user clicks the disconnect button for a social account.
     *
     * @param {Event} e The event object triggered by the disconnect button click. This is used to identify the current button and associated provider.
     * @return {Promise<void>} A promise that resolves when the disconnection process is completed, or rejects if an error occurs during the process.
     */
    async function onDisconnectClick(e) {
        const $btn = $(e.currentTarget);
        const provider = String($btn.data('social-disconnect') || '');
        if (!provider || !window.confirm(CFG.i18n.confirmDisconnect)) return;

        setBtnLoading($btn, true);
        try {
            const res = await fetch(CFG.accountsApiBase + '/' + encodeURIComponent(provider), {
                method: 'DELETE',
                credentials: 'include'
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const detail = data?.detail;
                throw Object.assign(
                    new Error(typeof detail === 'string' ? detail : CFG.i18n.fallback),
                    {status: res.status}
                );
            }
            renderSocialAccounts(data);
            showSuccess(CFG.i18n.disconnectSuccess);
        } catch (error) {
            handleError(error);
        } finally {
            setBtnLoading($btn, false);
        }
    }

    /**
     * Dynamically loads a script into the document.
     *
     * @param {string} src - The source URL of the script to load.
     * @param {Object} [options] - Optional configuration for the script element.
     * @param {boolean} [options.async=true] - Indicates if the script should be loaded asynchronously.
     * @param {boolean} [options.defer=true] - Indicates if the script should be deferred until after the document has been parsed.
     * @param {string} [options.id] - An optional ID to assign to the script element. If an element with the same ID already exists, the script won't be reloaded.
     * @return {Promise<void>} A promise that resolves when the script is successfully loaded or rejects in case of an error.
     */
    function loadScript(src, {async = true, defer = true, id} = {}) {
        return new Promise((resolve, reject) => {
            if (id && document.getElementById(id)) return resolve();
            const s = document.createElement('script');
            s.src = src;
            s.async = async;
            s.defer = defer;
            if (id) s.id = id;
            s.onload = resolve;
            s.onerror = reject;
            document.head.appendChild(s);
        });
    }

    let gtokenClient = null;

    /**
     * Ensures that the Google OAuth2 token client is initialized. If the client already exists, it returns immediately.
     * Otherwise, it loads the required Google library and initializes the token client with the specified configuration,
     * including callback handling for the authentication response.
     *
     * @return {Promise<void>} A promise that resolves when the Google token client is successfully initialized.
     */
    async function ensureGoogle() {
        if (gtokenClient) return;
        await loadScript('https://accounts.google.com/gsi/client', {id: 'google-gsi'});
        gtokenClient = google.accounts.oauth2.initTokenClient({
            client_id: CFG.googleClientId,
            scope: 'openid email profile',
            callback: async (resp) => {
                try {
                    const access_token = resp && resp.access_token;
                    if (!access_token) throw new Error(CFG.i18n.tokenInvalid);
                    const data = await postSocialLogin({vendor: 'google', token: access_token});
                    handleSuccess(data);
                } catch (e) {
                    handleError(e);
                } finally {
                    setBtnLoading($('[data-social="google"], #btn-google'), false);
                    disableButtons(false);
                }
            }
        });
    }

    /**
     * Handles the click event on the Google sign-in button, triggering the Google OAuth flow.
     *
     * @param {Event} e - The event object associated with the button click action.
     * @return {Promise<void>} A promise that resolves when the Google OAuth flow is successfully initiated,
     *                         or rejects if an error occurs during the process.
     */
    async function onGoogleClick(e) {
        e.preventDefault();
        const $btn = $(e.currentTarget);
        disableButtons();
        try {
            setBtnLoading($btn, true);
            await ensureGoogle();
            gtokenClient.requestAccessToken({prompt: 'consent'});
        } catch (e2) {
            setBtnLoading($btn, false);
            disableButtons(false);
            handleError(e2);
        }
    }

    /**
     * Ensures that the AppleID authentication script is loaded and initialized.
     * If the AppleID authentication library is already available, it will not reload it.
     * Otherwise, it will dynamically load the necessary script and initialize the AppleID SDK with the provided configuration.
     *
     * @return {Promise<void>} A promise that resolves once the script is loaded and initialized successfully.
     */
    async function ensureApple() {
        if (window.AppleID?.auth) return;
        await loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js', {id: 'apple-js'});
        AppleID.auth.init({
            clientId: CFG.appleClientId,
            scope: 'name email',
            redirectURI: CFG.appleRedirect,
            usePopup: true
        });
    }

    /**
     * Handles the click event for an Apple login button. Initiates the Apple sign-in process and processes the response.
     *
     * @param {Event} e - The event object for the click event.
     * @return {Promise<void>} Resolves when the login process is completed or an error is handled.
     */
    async function onAppleClick(e) {
        e.preventDefault();
        const $btn = $(e.currentTarget);
        disableButtons();
        try {
            setBtnLoading($btn, true);
            await ensureApple();
            const res = await AppleID.auth.signIn(); // popup
            const idToken = res?.authorization?.id_token;
            const first = res?.user?.name?.firstName || undefined;
            const last = res?.user?.name?.lastName || undefined;
            if (!idToken) throw new Error(CFG.i18n.tokenInvalid);
            const data = await postSocialLogin({vendor: 'apple', token: idToken, first_name: first, last_name: last});
            handleSuccess(data);
        } catch (e2) {
            if (String(e2?.message || '').toLowerCase().includes('popup')) {
                showGlobalError(CFG.i18n.popupBlocked);
            } else {
                handleError(e2);
            }
        } finally {
            setBtnLoading($btn, false);
            disableButtons(false);
        }
    }

    let fbReady = false;

    /**
     * Ensures that the Facebook SDK is loaded and initialized.
     * This function checks if the Facebook SDK is ready and properly initialized. If not, it initializes the SDK
     * with the specified appId and configuration settings. The function waits until the SDK is fully loaded
     * before resolving.
     *
     * @return {Promise<void>} A promise that resolves when the Facebook SDK is successfully loaded and initialized.
     */
    async function ensureFacebook() {
        if (fbReady && window.FB) return;
        await new Promise((resolve, reject) => {
            window.fbAsyncInit = function () {
                FB.init({
                    appId: CFG.facebookAppId,
                    cookie: true,
                    xfbml: false,
                    version: 'v19.0'
                });
                fbReady = true;
                resolve();
            };
            loadScript('https://connect.facebook.net/en_US/sdk.js', {id: 'fb-sdk'}).catch(reject);
        });
    }

    /**
     * Handles the click event for the "Login with Facebook" button.
     * Initiates the Facebook login process, posts the obtained access token
     * for social login, and processes the response or handles errors accordingly.
     *
     * @param {Event} e The event object triggered by the button click.
     * @return {Promise<void>} A promise that resolves when the Facebook login sequence is completed.
     */
    async function onFacebookClick(e) {
        e.preventDefault();
        const $btn = $(e.currentTarget);
        disableButtons();
        try {
            setBtnLoading($btn, true);
            await ensureFacebook();
            FB.login(async (response) => {
                try {
                    if (!response || !response.authResponse) {
                        throw new Error(CFG.i18n.facebookCancelled);
                    }
                    const accessToken = response.authResponse.accessToken;
                    const data = await postSocialLogin({vendor: 'facebook', token: accessToken});
                    handleSuccess(data);
                } catch (e3) {
                    handleError(e3);
                } finally {
                    setBtnLoading($btn, false);
                    disableButtons(false);
                }
            }, {scope: 'email,public_profile'});
        } catch (e2) {
            setBtnLoading($btn, false);
            disableButtons(false);
            handleError(e2);
        }
    }

    /**
     * Disables or enables specific buttons on the interface.
     *
     * @param {boolean} [disable=true] - Indicates whether the buttons should be disabled. Defaults to true.
     * @return {void} This method does not return a value.
     */
    function disableButtons(disable = true) {
        $('[data-social="google"], #btn-google').prop('disabled', disable);
        $('[data-social="apple"],  #btn-apple').prop('disabled', disable);
        $('[data-social="facebook"], #btn-facebook').prop('disabled', disable);
        $('[data-social-disconnect]').prop('disabled', disable);
        $('#reg-submit, #login-submit').prop('disabled', disable);
    }

    // init
    $(function () {
        $(document).on('click', '[data-social="google"], #btn-google', onGoogleClick);
        $(document).on('click', '[data-social="apple"], #appleid-signin', onAppleClick);
        $(document).on('click', '[data-social="facebook"], #btn-facebook', onFacebookClick);
        $(document).on('click', '[data-social-disconnect]', onDisconnectClick);
        if (CFG.mode === 'account') loadSocialAccounts().catch(handleError);
    });

    $(function () {
        Object.entries(PROVIDER_ENABLED).forEach(([provider, enabled]) => {
            if (enabled) return;
            $('[data-social="' + provider + '"]').prop('disabled', true).hide();
            $('[data-social-account="' + provider + '"]').hide();
        });

        $('.actions').has('[data-social]').each(function () {
            const $actions = $(this);
            if ($actions.find('[data-social]:visible').length) return;
            $actions.hide();
            $actions.prev('.or-divider').add($actions.next('.or-divider')).hide();
        });
    });

})(jQuery, window, document);
